/**
 * i18next基本設定
 *
 * @file i18n.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * YAMLファイルベースのi18next基本初期化設定。動的namespace解決システム実装。
 * lazyWithI18nヘルパー関数を統合し、コード分割とi18next初期化を最適化。
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import * as yaml from 'js-yaml';
import React from 'react';
import { initReactI18next } from 'react-i18next';

// コンポーネント駆動型namespace管理システム
const loadedNamespaces = new Set<string>([]);
const namespaceKeyMap = new Map<string, string>();
const componentNamespaceRegistry: Set<string> = new Set<string>([]);
const registrationQueue = new Set<string>(); // 初期化前の登録キュー

// サポート言語の定義
export const SUPPORTED_LANGUAGES = {
  ja: 'Japanese',
  ko: 'Korean',
  en: 'English',
  zh: 'Chinese'
} as const;

// サポートされている言語コードの配列（共通定数）
export const SUPPORTED_LANGUAGE_CODES = Object.keys(SUPPORTED_LANGUAGES) as SupportedLanguage[];

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// ロール別サポート言語定義
const SUPPORTED_LANGS_BY_ROLE = {
  buyer: ['ja', 'en', 'ko', 'zh'] as SupportedLanguage[],
  seller: ['ja', 'ko'] as SupportedLanguage[],
  admin: ['ja', 'ko'] as SupportedLanguage[],
} as const;

export type I18nRole = keyof typeof SUPPORTED_LANGS_BY_ROLE;

// 動的namespace検出システム（コンポーネント駆動のみ使用）
export const discoverNamespaces = async (): Promise<string[]> => {
  // registerNamespaces()により登録されたnamespaceのみを返す
  return Array.from(componentNamespaceRegistry);
};

// キープレフィックスからnamespace解決関数（完全コンポーネント駆動）
export const resolveNamespaceFromKey = (key: string): string => {
  // 基本的なプレフィックス解決
  if (key.startsWith('common.')) return 'common';
  if (key.startsWith('error.')) return 'error';

  // layout.プレフィックスの処理（動的解決）
  if (key.startsWith('layout.')) {
    const layoutNamespaces = Array.from(loadedNamespaces).filter(ns => ns.endsWith('Layout'));
    return layoutNamespaces[0];
  }

  // その他のキーはコンポーネント登録されたページnamespaceから選択（動的解決）
  const pageNamespaces = Array.from(loadedNamespaces).filter(ns =>
    ns !== 'common' && ns !== 'error' && !ns.endsWith('Layout')
  );

  // 現在のページURLから最適なnamespaceを推定（最優先）
  if (typeof window !== 'undefined' && pageNamespaces.length > 0) {
    const pathname = window.location.pathname;

    // URLパスからnamespace名を推定
    const pathSegment = pathname.split('/')[1] || 'home';

    // 推定されたnamespaceが登録済みかチェック（優先度順）
    const matchedNamespace = pageNamespaces.find(ns =>
      ns.toLowerCase().includes(pathSegment.toLowerCase())
    );

    // マッチしたページnamespaceを最優先で返す（commonより優先）
    if (matchedNamespace) {
      return matchedNamespace;
    }
  }

  // フォールバック: 最初に登録されたページnamespace（common以外を優先）
  return pageNamespaces[0];
};

// ファイルパス解決関数
const resolveFilePath = (lng: string, ns: string): string => {
  // common/errorsはfrontend-shared/から読み込む
  if (ns === 'common' || ns === 'errors') {
    return `/shared/locales/${lng}/${ns}.yaml`;
  }
  // その他は各フロントエンドのlocalesから読み込む
  return `/locales/${lng}/${ns}.yaml`;
};

// コンポーネント駆動namespace管理システム
let discoveredNamespaces: string[] = [];
let isI18nInitialized = false;
let initializationPromise: Promise<void> | null = null;

// i18next初期化システム（完全コンポーネント駆動・遅延初期化）
const initializeI18nCore = async (role: I18nRole = 'buyer', defaultLanguage?: string): Promise<void> => {
  if (isI18nInitialized) {
    return;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  const supportedLangs = SUPPORTED_LANGS_BY_ROLE[role];

  initializationPromise = (async () => {
    try {
      // コンポーネント登録されたnamespaceのみを使用
      const componentRegistered = Array.from(componentNamespaceRegistry);
      const queuedNamespaces = Array.from(registrationQueue);

      // コンポーネント駆動のnamespaceのみを統合
      const allNamespaces = [...new Set([...componentRegistered, ...queuedNamespaces])];
      discoveredNamespaces = allNamespaces;

      // コンポーネント駆動のnamespaceでi18nextを初期化
      await i18n
        .use(Backend)
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
          fallbackLng: defaultLanguage !== undefined ? defaultLanguage : 'ja',
          supportedLngs: [...supportedLangs],
          nonExplicitSupportedLngs: true,
          load: 'languageOnly',
          debug: false,
          ns: discoveredNamespaces, // コンポーネント駆動のnamespaceのみ
          defaultNS: false,
          initImmediate: false,

          interpolation: {
            escapeValue: false,
          },

          backend: {
            loadPath: (languages: string[], namespaces: string[]) => {
              const lng = languages[0];
              const ns = namespaces[0];
              const path = resolveFilePath(lng, ns);
              return path;
            },
            parse: (data: string) => {
              try {
                const parsed = yaml.load(data);

                // デバッグログ（開発時のみ）
                if (process.env.NODE_ENV === 'development') {
                  console.log('[i18n] YAML parsed:', parsed);
                }

                return parsed;
              } catch (error) {
                console.error('YAML parse error:', error);
                return {};
              }
            },
            crossDomain: false,
            withCredentials: false,
            request: (_options: any, url: string, _payload: any, callback: any) => {
              fetch(url)
                .then(async response => {
                  if (!response.ok) {
                    // レスポンスボディの詳細エラーメッセージを取得
                    let errorDetail = '';
                    try {
                      const errorData = await response.json();
                      errorDetail = errorData?.detail || errorData?.message || '';
                    } catch {
                      // JSON解析失敗時は空文字
                    }

                    const errorMessage = errorDetail
                      ? `HTTP ${response.status}: ${errorDetail}`
                      : `HTTP ${response.status}`;

                    throw new Error(errorMessage);
                  }
                  return response.text();
                })
                .then(data => {
                  const trimmed = data.trimStart();
                  if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
                    throw new Error(`HTML received instead of YAML (likely missing file): ${url}`);
                  }
                  callback(null, { status: 200, data });
                })
                .catch(error => {
                  console.error(`Failed to fetch ${url}:`, error);
                  callback(error, null);
                });
            },
          },

          detection: {
            order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
            caches: ['localStorage'],
            lookupLocalStorage: 'i18nextLng',
            lookupQuerystring: 'lang',
          },

          react: {
            useSuspense: true,
          },

          saveMissing: false,
          missingKeyHandler: false,
        });

      // コンポーネント駆動のnamespaceを管理システムに登録
      discoveredNamespaces.forEach(ns => {
        loadedNamespaces.add(ns);
        componentNamespaceRegistry.add(ns);
      });

      isI18nInitialized = true;

      // 初期化完了後、registrationQueueに追加された全namespaceをロード
      if (registrationQueue.size > 0) {
        console.log('[i18n] Loading queued namespaces after initialization:', Array.from(registrationQueue));

        for (const namespace of registrationQueue) {
          try {
            await i18n.loadNamespaces(namespace);
            loadedNamespaces.add(namespace);
            componentNamespaceRegistry.add(namespace);
            console.log(`[i18n] Successfully loaded queued namespace: ${namespace}`);
          } catch (error) {
            console.error(`[i18n] Failed to load queued namespace "${namespace}":`, error);
          }
        }

        // ロード完了後、キューをクリア
        registrationQueue.clear();
        console.log('[i18n] All queued namespaces loaded and queue cleared');
      } else {
        // キューが空の場合もクリア
        registrationQueue.clear();
      }

      if (typeof window !== 'undefined') {
        (window as any).i18next = i18n;
        (window as any).discoveredNamespaces = discoveredNamespaces;

        // デバッグログ（開発時のみ）
        if (process.env.NODE_ENV === 'development') {
          console.log('[i18n] Initialization complete. Resources:', i18n.store.data);
          console.log('[i18n] Registered namespaces:', discoveredNamespaces);
        }
      }

    } catch (error) {
      console.error('[i18n] ❌ Component-driven i18next initialization error:', error);
      throw error;
    }
  })();

  return initializationPromise;
};

// コンポーネント駆動型namespace登録システム
export const registerNamespaces = (namespaces: string[]): Promise<void> => {
  // デバッグログ（開発時のみ）
  if (process.env.NODE_ENV === 'development') {
    console.log('[i18n] registerNamespaces called with:', namespaces, 'isInitialized:', isI18nInitialized);
  }

  // 初期化されていない場合、先に初期化を実行
  if (!isI18nInitialized) {
    console.log('[i18n] i18next not initialized, calling ensureInitialized()');

    // 一旦キューに追加
    namespaces.forEach(ns => {
      const namespace = ns.startsWith('layout/') ? ns : ns;
      registrationQueue.add(namespace);
      console.log(`[i18n] Namespace "${namespace}" added to registration queue`);
    });

    // 初期化完了後に再登録し、そのPromiseを返す
    return ensureInitialized().then(() => {
      console.log('[i18n] Initialization completed, re-registering namespaces');
      return registerNamespaces(namespaces);
    });
  }

  const loadPromises: Promise<void>[] = [];

  namespaces.forEach(namespace => {
    // グローバルレジストリに追加
    componentNamespaceRegistry.add(namespace);

    // i18n初期化済みの場合は即座に反映
    if (isI18nInitialized) {
      // 既にロード済みの場合はスキップ
      if (!loadedNamespaces.has(namespace)) {
        loadedNamespaces.add(namespace);

        // i18nextの設定を動的更新
        const currentNs = Array.isArray(i18n.options.ns) ? i18n.options.ns : [];
        const allNamespaces = [...new Set([...currentNs, namespace])];
        i18n.options.ns = allNamespaces;

        // デバッグログ（開発時のみ）
        if (process.env.NODE_ENV === 'development') {
          console.log(`[i18n] Loading namespace dynamically: "${namespace}"`);
        }

        // 動的にnamespaceをロード（重要：YAMLファイルの実ロード）
        loadPromises.push(
          i18n.loadNamespaces(namespace).catch(error => {
            console.error(`[i18n] ❌ Failed to load namespace "${namespace}":`, error);
          })
        );
      } else {
        // デバッグログ（開発時のみ）
        if (process.env.NODE_ENV === 'development') {
          console.log(`[i18n] Namespace "${namespace}" already loaded, skipping`);
        }
      }
    } else {
      // 初期化前の場合はキューに追加
      registrationQueue.add(namespace);

      // デバッグログ（開発時のみ）
      if (process.env.NODE_ENV === 'development') {
        console.log(`[i18n] Namespace "${namespace}" added to registration queue`);
      }
    }
  });

  // 全namespaceのロード完了を待機するPromiseを返す
  return Promise.all(loadPromises).then(() => {});
};

// 従来のregisterNamespace関数（下位互換性）
export const registerNamespace = (namespace: string, keyPrefixes: string[] = []): void => {
  registerNamespaces([namespace]);

  // キープレフィックスとnamespaceのマッピング追加
  keyPrefixes.forEach(prefix => {
    namespaceKeyMap.set(prefix, namespace);
  });
};

// 明示的な名前空間ロード関数（簡素化）
export const ensureNamespacesLoaded = async (namespaces: string[]): Promise<void> => {
  const namespacesToLoad = namespaces.filter(ns => !loadedNamespaces.has(ns));

  if (namespacesToLoad.length > 0) {
    try {
      await Promise.all(namespacesToLoad.map(async (ns) => {
        await i18n.loadNamespaces(ns);
        loadedNamespaces.add(ns);
      }));
    } catch (error) {
      console.error('Failed to load namespaces:', namespacesToLoad, error);
      throw error;
    }
  }
};

// ロード済みnamespace一覧取得
export const getLoadedNamespaces = (): string[] => {
  return Array.from(loadedNamespaces);
};

// コンポーネント駆動で管理されたnamespace一覧取得
export const getDiscoveredNamespaces = (): string[] => {
  return [...discoveredNamespaces];
};

// コンポーネント駆動型namespace管理を初期化
export const ensureComponentNamespaceRegistry = () => {
  (componentNamespaceRegistry as Set<string>).clear?.();

  // デバッグログ（開発時のみ）
  if (process.env.NODE_ENV === 'development') {
    console.log(`[i18n] Namespace initialized`);
  }
};

// コンポーネント駆動型namespace取得
export const getRegisteredNamespaces = (): string[] => {
  return Array.from(componentNamespaceRegistry);
};

// コンポーネント駆動namespace統計
export const getNamespaceStats = () => {
  return {
    registered: componentNamespaceRegistry.size,
    loaded: loadedNamespaces.size,
    queued: registrationQueue.size,
    total: discoveredNamespaces.length
  };
};

// i18next初期化状態確認
export const isInitialized = (): boolean => {
  return isI18nInitialized && i18n.isInitialized;
};

// 手動初期化用関数（コンポーネント駆動）
export const reinitializeWithLanguage = async (language: SupportedLanguage): Promise<void> => {
  if (!isI18nInitialized) {
    await initializeI18nCore();
  } else {
    await changeLanguage(language);
  }
};

// ロール別初期化関数（各フロントエンドのmain.tsxから呼び出し）
export const initializeI18n = (role: I18nRole, defaultLanguage?: string): Promise<void> => {
  return initializeI18nCore(role, defaultLanguage);
};

// 明示的初期化関数（レイアウトコンポーネントから呼び出し）
export const ensureInitialized = async (): Promise<void> => {
  console.log('[i18n] ensureInitialized called, isI18nInitialized:', isI18nInitialized);
  if (!isI18nInitialized && !initializationPromise) {
    console.log('[i18n] Starting initializeI18nCore...');
    await initializeI18nCore();
    console.log('[i18n] initializeI18nCore completed');
  } else if (initializationPromise) {
    console.log('[i18n] Waiting for existing initialization...');
    await initializationPromise;
    console.log('[i18n] Existing initialization completed');
  } else {
    console.log('[i18n] Already initialized');
  }

  // registrationQueueの処理はinitializeI18nCore()内で実行済み
};

i18n.on('initialized', () => {
});

// ブラウザ環境でのアクセス用にグローバルに公開
if (typeof window !== 'undefined') {
  (window as any).i18next = i18n;
  (window as any).resolveNamespaceFromKey = resolveNamespaceFromKey;
  (window as any).discoveredNamespaces = discoveredNamespaces;
}

// 動的namespace解決付きt関数（遅延初期化対応）
export const t = (key: string, options?: any): string => {
  // i18next初期化前の場合は初期化を待機
  if (!isI18nInitialized) {
    return key;
  }

  // キーから適切なnamespaceを解決
  const resolvedNamespace = resolveNamespaceFromKey(key);

  // 解決されたnamespaceで翻訳実行
  try {
    const result = i18n.t(key, { ...options, ns: resolvedNamespace });

    // 翻訳が見つからない場合（キーがそのまま返される）はキーを返す
    if (result === key) {
      return key;
    }

    return result as string;
  } catch (error) {
    console.error('Translation error:', error);
    return key;
  }
};

export const changeLanguage = async (language: SupportedLanguage) => {
  try {
    await i18n.changeLanguage(language);
    return true;
  } catch (error) {
    console.error('Failed to change language:', error);
    return false;
  }
};

// TranslationFiles型定義
export type TranslationFiles = string[];

// 翻訳システム準備完了待機システム
let translationSystemReady = false;
const translationReadyCallbacks: (() => void)[] = [];

export const waitForTranslationReady = (): Promise<void> => {
  return new Promise((resolve) => {
    if (translationSystemReady && isI18nInitialized) {
      resolve();
    } else {
      translationReadyCallbacks.push(resolve);
    }
  });
};

// 翻訳システム準備完了通知
const notifyTranslationReady = () => {
  translationSystemReady = true;
  translationReadyCallbacks.forEach(callback => callback());
  translationReadyCallbacks.length = 0;
};

// i18next初期化完了時に翻訳システム準備完了を通知
i18n.on('initialized', () => {
  setTimeout(() => {
    notifyTranslationReady();
  }, 100); // バッファ時間
});

// createPageTranslation関数
export const createPageTranslation = (translationFiles: TranslationFiles) => {
  // namespace登録を確実に実行
  registerNamespaces(translationFiles);

  return (key: string, options?: any): string => {
    return t(key, options);
  };
};

/**
 * 多言語化対応のReact.lazyヘルパー関数（v3.2.0標準実装）
 *
 * React.lazyによるコード分割を維持しつつ、該当画面アクセス時のみi18next namespaceを登録する。
 * レイアウト分離アーキテクチャ（方式B）と統合し、100画面対応でも初期ロード時のパフォーマンスを維持。
 *
 * @param importFn - 動的インポート関数
 * @param namespaces - ロードするnamespaceリスト（例: ['PageName', 'LayoutName', 'common']）
 * @returns React.lazy実行結果
 *
 * @example
 * const YauctionProductDetailPage = lazyWithI18n(
 *   () => import('../pages/public/YauctionProductDetailPage'),
 *   ['YauctionProductDetailPage', 'PublicLayout', 'common']
 * );
 */
export const lazyWithI18n = (
  importFn: () => Promise<any>,
  namespaces: string[]
) => {
  // React.lazy()でコンポーネントをロード（factory関数は初回のみでOK）
  const LazyComponent = React.lazy(importFn);

  // ラッパーコンポーネント：毎回レンダリングされる
  const WrappedComponent: React.FC<any> = (props) => {
    const [nsLoaded, setNsLoaded] = React.useState(false);

    // コンポーネントマウント時に必ずnamespaceを登録し、完了を待つ
    React.useEffect(() => {
      console.log('[lazyWithI18n] Component mounted, registering namespaces:', namespaces);
      ensureComponentNamespaceRegistry();
      registerNamespaces(namespaces).then(() => {
        setNsLoaded(true);
      });
    }, []); // 空の依存配列 = マウント時のみ実行

    if (!nsLoaded) {
      return null;
    }

    return <LazyComponent {...props} />;
  };

  WrappedComponent.displayName = 'LazyWithI18n(Component)';

  return WrappedComponent;
};

export default i18n;
